import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Loader2, File } from 'lucide-react';
import { toast } from 'sonner';
import { uploadSecondFile } from '../../utils/api';
import { useT } from '../../i18n';

/**
 * The drop area for a second file, shared by merge, append and compare.
 *
 * It owns the upload itself — including surfacing the parser's warnings, which
 * matter as much on the second file as on the first: skipped rows, a guessed
 * separator or an ignored Excel sheet are silent data loss either way. What
 * each operation then does with the result differs, so that part is the
 * caller's, passed as onUploaded.
 */
const SecondFileDropzone = ({ label, onUploaded }) => {
  const t = useT();
  const [uploading, setUploading] = useState(false);

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploading(true);
    try {
      const result = await uploadSecondFile(file);
      (result.warnings || []).forEach((warning) => toast.warning(warning, { duration: 8000 }));
      onUploaded(result, file);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || t('upload.failed'));
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
    multiple: false,
    disabled: uploading,
  });

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
      )}
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-[var(--radius)] p-8 text-center cursor-pointer transition-colors duration-200 ${
          isDragActive ? 'border-primary bg-muted' : 'border-border hover:border-primary'
        }`}
        data-testid="second-file-dropzone"
      >
        <input {...getInputProps()} disabled={uploading} />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <span className="text-sm text-foreground">{t('upload.uploading')}</span>
          </div>
        ) : isDragActive ? (
          <div className="flex flex-col items-center gap-2">
            <File className="h-10 w-10 text-primary" />
            <span className="text-sm text-foreground font-medium">{t('upload.drop')}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-10 w-10 text-muted-foreground" />
            <span className="text-sm text-foreground">{t('upload.cta')}</span>
            <span className="text-xs text-muted-foreground">{t('upload.browse')}</span>
            <span className="text-xs text-muted-foreground mt-2">{t('upload.formats')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecondFileDropzone;
